export type NavBadgeTone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

export type NavItem = {
  label: string;
  to: string;
  search?: Record<string, unknown>;
  /** Show only when module matches */
  module?: string[];
  /** Nested under a parent toggle (depth 1) */
  depth?: number;
  /** Optional short keyword aliases for sidebar search */
  keywords?: string[];
  /** Sidebar badge count key from useSidebarCounts */
  countKey?: string;
  badgeTone?: NavBadgeTone;
  /** Collapsible parent with children (Orders / Order Refunds) */
  children?: NavItem[];
  icon?: string;
  /** module_permission_check key — hide when the session user lacks it */
  permission?: string;
};

export type NavSection = {
  id: string;
  title: string;
  icon: string;
  items: NavItem[];
  defaultCollapsed?: boolean;
  /** Global admin module id — when set, section only shows under that navbar module */
  module?: string | string[];
};

const ORDER_STATUS_ITEMS: NavItem[] = [
  {
    label: 'All',
    to: '/orders',
    search: { status: 'all' },
    depth: 1,
    countKey: 'all',
    badgeTone: 'info',
    keywords: ['list', 'every'],
  },
  {
    label: 'Scheduled',
    to: '/orders',
    search: { status: 'scheduled' },
    depth: 1,
    countKey: 'scheduled',
    badgeTone: 'info',
  },
  {
    label: 'Pending',
    to: '/orders',
    search: { status: 'pending' },
    depth: 1,
    countKey: 'pending',
    badgeTone: 'info',
    keywords: ['waiting'],
  },
  {
    label: 'Accepted',
    to: '/orders',
    search: { status: 'accepted' },
    depth: 1,
    countKey: 'accepted',
    badgeTone: 'success',
  },
  {
    label: 'Processing',
    to: '/orders',
    search: { status: 'processing' },
    depth: 1,
    countKey: 'processing',
    badgeTone: 'warning',
  },
  {
    label: 'Order On The Way',
    to: '/orders',
    search: { status: 'item_on_the_way' },
    depth: 1,
    countKey: 'item_on_the_way',
    badgeTone: 'warning',
    keywords: ['out for delivery', 'picked up'],
  },
  {
    label: 'Delivered',
    to: '/orders',
    search: { status: 'delivered' },
    depth: 1,
    countKey: 'delivered',
    badgeTone: 'success',
  },
  {
    label: 'Canceled',
    to: '/orders',
    search: { status: 'canceled' },
    depth: 1,
    countKey: 'canceled',
    badgeTone: 'danger',
  },
  {
    label: 'Payment Failed',
    to: '/orders',
    search: { status: 'failed' },
    depth: 1,
    countKey: 'failed',
    badgeTone: 'danger',
    keywords: ['failed payment'],
  },
  {
    label: 'Refunded',
    to: '/orders',
    search: { status: 'refunded' },
    depth: 1,
    countKey: 'refunded',
    badgeTone: 'danger',
  },
  {
    label: 'Offline Payments',
    to: '/offline-payments',
    depth: 1,
    countKey: 'offline_payments',
    badgeTone: 'success',
    keywords: ['verify', 'cash', 'offline'],
  },
];

/**
 * Dispatch Management operational control center.
 * Only two visible sections: Live Tracking + Orders.
 */
const DISPATCH_NAV: NavSection[] = [
  {
    id: 'dispatch',
    title: 'Dispatch Management',
    icon: 'truck',
    module: ['dispatch'],
    items: [
      {
        label: 'Live Tracking',
        to: '/dispatch-management/live-tracking',
        icon: 'map-pin',
        keywords: ['gps', 'map', 'drivers', 'fleet', 'tracking', 'subdrivers', 'realtime'],
      },
      {
        label: 'Orders',
        to: '/dispatch-management/orders',
        search: { status: 'all' },
        icon: 'shopping-cart',
        keywords: ['orders', 'assign', 'delivery', 'dispatch', 'lifecycle'],
      },
    ],
  },
];

/** Mirrors layouts/admin/partials/_sidebar_*.blade.php */
export const ADMIN_NAV: NavSection[] = [
  ...DISPATCH_NAV,
  {
    id: 'main',
    title: 'Overview',
    icon: 'layout-dashboard',
    items: [
      { label: 'Dashboard', to: '/dashboard', icon: 'activity', keywords: ['home', 'stats', 'kpi'] },
      { label: 'New Sale (POS)', to: '/pos', icon: 'calculator', keywords: ['point of sale', 'cashier'] },
      { label: 'Messages', to: '/messages', icon: 'message', keywords: ['chat', 'inbox', 'conversation'] },
    ],
  },
  {
    id: 'orders',
    title: 'Order Management',
    icon: 'clipboard-list',
    items: [
      {
        label: 'Orders',
        to: '/orders',
        search: { status: 'all' },
        icon: 'shopping-cart',
        children: ORDER_STATUS_ITEMS,
        keywords: ['orders', 'status'],
      },
      {
        label: 'Order Refunds',
        to: '/refunds',
        icon: 'receipt',
        children: [
          {
            label: 'Refund Requests',
            to: '/refunds',
            search: { status: 'requested' },
            depth: 1,
            countKey: 'refund_requested',
            badgeTone: 'danger',
            keywords: ['refund', 'money back'],
          },
        ],
        keywords: ['refunds', 'reject'],
      },
      {
        label: 'Flash Sales',
        to: '/flash-sales',
        icon: 'zap',
        keywords: ['flash', 'deal'],
      },
    ],
  },
  {
    id: 'parcel',
    title: 'Parcel',
    icon: 'package-open',
    items: [
      { label: 'Parcel Orders', to: '/parcels', search: { status: 'all' }, icon: 'package', module: ['parcel'] },
      { label: 'Parcel Category', to: '/parcel-categories', icon: 'tags', module: ['parcel'] },
      { label: 'Parcel Settings', to: '/parcel-settings', icon: 'settings-2', module: ['parcel'] },
    ],
  },
  {
    id: 'promotion',
    title: 'Promotion Management',
    icon: 'gift',
    items: [
      {
        label: 'Campaigns',
        to: '/campaigns/basic',
        icon: 'target',
        keywords: ['campaign'],
        children: [
          {
            label: 'Basic Campaigns',
            to: '/campaigns/basic',
            depth: 1,
            keywords: ['basic'],
          },
          {
            label: 'Item Campaigns',
            to: '/campaigns/item',
            depth: 1,
            keywords: ['food campaign', 'item'],
          },
        ],
      },
      {
        label: 'Banners',
        to: '/banners',
        icon: 'image',
        keywords: ['banner', 'image'],
      },
      {
        label: 'Other Banners',
        to: '/other-banners',
        icon: 'panels',
        keywords: ['promotional banner', 'why choose'],
      },
      {
        label: 'Coupons',
        to: '/coupons',
        icon: 'ticket-percent',
        keywords: ['discount', 'promo', 'gift'],
      },
      {
        label: 'Push Notification',
        to: '/notifications',
        icon: 'bell-ring',
        keywords: ['fcm', 'push', 'notification'],
      },
      {
        label: 'Advertisement',
        to: '/advertisements',
        icon: 'megaphone',
        keywords: ['ads', 'advertisement'],
        children: [
          {
            label: 'New Advertisement',
            to: '/advertisements/new',
            depth: 1,
            keywords: ['create', 'add'],
          },
          {
            label: 'Ad Requests',
            to: '/advertisements/requests',
            depth: 1,
            keywords: ['requests', 'pending'],
          },
          {
            label: 'Ads List',
            to: '/advertisements',
            depth: 1,
            keywords: ['list', 'all ads'],
          },
        ],
      },
    ],
  },
  {
    id: 'product',
    title: 'Product Management',
    icon: 'package',
    items: [
      {
        label: 'Categories',
        to: '/categories',
        icon: 'folder-tree',
        keywords: ['category', 'sub category'],
        children: [
          { label: 'Category', to: '/categories', depth: 1, keywords: ['main category'] },
          { label: 'Sub Category', to: '/sub-categories', depth: 1, keywords: ['subcategory'] },
          {
            label: 'Bulk Import',
            to: '/categories/bulk-import',
            depth: 1,
            keywords: ['import', 'csv', 'excel'],
          },
          {
            label: 'Bulk Export',
            to: '/categories/bulk-export',
            depth: 1,
            keywords: ['export', 'download'],
          },
        ],
      },
      {
        label: 'Attributes',
        to: '/attributes',
        icon: 'list',
        keywords: ['variation', 'choice', 'size', 'color'],
      },
      {
        label: 'Units',
        to: '/units',
        icon: 'ruler',
        keywords: ['kg', 'piece', 'measure'],
      },
      {
        label: 'Product Setup',
        to: '/items',
        icon: 'package-search',
        keywords: ['items', 'food', 'products', 'menu'],
        children: [
          {
            label: 'List',
            to: '/items',
            depth: 1,
            keywords: ['item list', 'products'],
          },
          {
            label: 'Product Gallery',
            to: '/items/gallery',
            depth: 1,
            keywords: ['gallery', 'images'],
          },
          {
            label: 'New Item Requests',
            to: '/items/approvals',
            depth: 1,
            keywords: ['approval', 'pending items'],
          },
          {
            label: 'Reviews',
            to: '/item-reviews',
            depth: 1,
            keywords: ['ratings', 'feedback'],
          },
          {
            label: 'Bulk Export',
            to: '/items/bulk-export',
            depth: 1,
            keywords: ['export products'],
          },
        ],
      },
      // Keep module-specific catalog entries reachable
      {
        label: 'Addon Categories',
        to: '/addon-categories',
        icon: 'folder-plus',
        keywords: ['addons', 'choices'],
      },
      {
        label: 'Addons',
        to: '/addons',
        icon: 'puzzle',
        keywords: ['extra', 'topping'],
      },
      {
        label: 'Brands',
        to: '/brands',
        icon: 'badge-check',
        module: ['ecommerce'],
        keywords: ['brand'],
      },
      {
        label: 'Common Conditions',
        to: '/common-conditions',
        icon: 'heart-pulse',
        module: ['pharmacy'],
        keywords: ['condition', 'pharmacy'],
      },
    ],
  },
  {
    id: 'store',
    title: 'Store Management',
    icon: 'building-2',
    items: [
      {
        label: 'New Stores',
        to: '/stores/pending',
        icon: 'file-text',
        keywords: ['pending', 'requests', 'new restaurants'],
      },
      {
        label: 'Add Store',
        to: '/stores/new',
        icon: 'circle-plus',
        keywords: ['create', 'new restaurant'],
      },
      {
        label: 'Stores List',
        to: '/stores',
        icon: 'store',
        keywords: ['restaurants', 'vendors', 'list'],
      },
      {
        label: 'Recommended Store',
        to: '/stores/recommended',
        icon: 'star',
        keywords: ['recommended', 'featured'],
      },
      {
        label: 'Bulk Import',
        to: '/stores/bulk-import',
        icon: 'file-up',
        keywords: ['import', 'excel', 'csv'],
      },
      {
        label: 'Bulk Export',
        to: '/stores/bulk-export',
        icon: 'file-down',
        keywords: ['export', 'download'],
      },
    ],
  },
  {
    id: 'users',
    title: 'People',
    icon: 'users-round',
    items: [
      { label: 'Drivers', to: '/drivers', icon: 'bike', keywords: ['drivers', 'fleet', 'delivery', 'tracking', 'gps', 'map', 'couriers'] },
      { label: 'Delivery Men', to: '/deliverymen', icon: 'bike', keywords: ['dm', 'couriers', 'drivers', 'add', 'create', 'manage', 'pending', 'applications'] },
      { label: 'DM Reviews', to: '/deliverymen/reviews', icon: 'star' },
      { label: 'Vehicles', to: '/vehicles', icon: 'car' },
      { label: 'Customers', to: '/customers', icon: 'contact-round', keywords: ['users', 'clients'] },
      { label: 'Wallet Report', to: '/customers/wallet', icon: 'wallet' },
      { label: 'Loyalty Points', to: '/customers/loyalty', icon: 'award' },
      { label: 'Wallet Bonus', to: '/wallet-bonus', icon: 'hand-coins' },
      { label: 'Cashback', to: '/cashback', icon: 'coins' },
      { label: 'Employees', to: '/employees', icon: 'briefcase-business', keywords: ['staff'] },
      { label: 'Custom Roles', to: '/roles', icon: 'shield-check', keywords: ['permissions', 'rbac'] },
      { label: 'Contacts', to: '/contacts', icon: 'mail' },
    ],
  },
  {
    id: 'transactions',
    title: 'Business Management',
    icon: 'wallet-cards',
    /** Only shown under the global Transactions & Reports module */
    module: ['transactions'],
    items: [
      // Central Stripe / financial operations hub
      { label: 'Financial Overview', to: '/transactions-reports/overview', icon: 'layout-dashboard', keywords: ['stripe', 'finance', 'overview', 'kpi', 'dashboard'], depth: 0 },
      { label: 'Customer Payments', to: '/transactions-reports/customer-payments', icon: 'credit-card', keywords: ['card', 'payment', 'customer', 'stripe'], depth: 0 },
      {
        label: 'Connected Accounts',
        to: '/transactions-reports/connected-accounts',
        icon: 'landmark',
        keywords: ['stripe', 'connect', 'vendor', 'driver', 'onboarding'],
        children: [
          { label: 'All Accounts', to: '/transactions-reports/connected-accounts', depth: 1, keywords: ['list'] },
          { label: 'Vendors', to: '/transactions-reports/connected-accounts', search: { type: 'vendor' }, depth: 1, keywords: ['stores'] },
          { label: 'Delivery Drivers', to: '/transactions-reports/connected-accounts', search: { type: 'driver' }, depth: 1, keywords: ['dm'] },
          { label: 'Onboarding Required', to: '/transactions-reports/connected-accounts', search: { status: 'ONBOARDING_REQUIRED' }, depth: 1 },
          { label: 'Verification Pending', to: '/transactions-reports/connected-accounts', search: { status: 'VERIFICATION_PENDING' }, depth: 1 },
          { label: 'Restricted Accounts', to: '/transactions-reports/connected-accounts', search: { status: 'RESTRICTED' }, depth: 1 },
        ],
      },
      {
        label: 'Transfers',
        to: '/transactions-reports/transfers',
        icon: 'arrow-left-right',
        keywords: ['transfer', 'vendor', 'driver', 'allocation'],
        children: [
          { label: 'All Transfers', to: '/transactions-reports/transfers', depth: 1 },
          { label: 'Vendor Transfers', to: '/transactions-reports/transfers/vendors', search: { type: 'vendor' }, depth: 1 },
          { label: 'Driver Transfers', to: '/transactions-reports/transfers/drivers', search: { type: 'driver' }, depth: 1 },
        ],
      },
      { label: 'Bank Payouts', to: '/transactions-reports/payouts', icon: 'banknote', keywords: ['payout', 'bank', 'stripe'], depth: 0 },
      // Existing financial destinations (preserved URLs)
      { label: 'Withdraw Requests', to: '/withdrawals/stores', icon: 'arrow-down-to-line', keywords: ['store', 'withdraw', 'requests'], permission: 'withdraw_list' },
      { label: 'Deliveryman Withdrawals', to: '/withdrawals/deliverymen', icon: 'bike', keywords: ['dm', 'withdraw'], permission: 'withdraw_list' },
      { label: 'Store Disbursement', to: '/disbursements/stores', icon: 'landmark', keywords: ['disbursement', 'store'], permission: 'disbursement' },
      { label: 'Delivery Man Disbursement', to: '/disbursements/deliverymen', icon: 'hand-coins', keywords: ['dm', 'disbursement'], permission: 'disbursement' },
      { label: 'Delivery Man Payments', to: '/dm-earnings', icon: 'coins', keywords: ['earnings', 'payments', 'provide'], permission: 'provide_dm_earning' },
      { label: 'Refunds', to: '/transactions-reports/refunds', icon: 'rotate-ccw', keywords: ['refund', 'chargeback'], depth: 0 },
      { label: 'Financial Reports', to: '/transactions-reports/reports', icon: 'chart-column', keywords: ['reports', 'analytics', 'export', 'financial'], permission: 'report', depth: 0 },
      { label: 'Stripe Webhooks', to: '/transactions-reports/webhooks', icon: 'webhook', keywords: ['stripe', 'webhook', 'events'], depth: 0 },
      { label: 'Payment Settings', to: '/transactions-reports/settings', icon: 'settings-2', keywords: ['stripe', 'gateway', 'settings'], permission: 'settings' },
      { label: 'Withdraw Method', to: '/withdraw-methods', icon: 'credit-card', keywords: ['methods', 'settings'], permission: 'settings' },
    ],
  },
  {
    id: 'reports',
    title: 'Report and Analytics',
    icon: 'chart-column',
    /** Blade _sidebar_transactions.blade.php Report section — under Transactions & Reports only */
    module: ['transactions'],
    items: [
      { label: 'Transaction Report', to: '/reports/transactions', icon: 'arrow-left-right', keywords: ['day', 'wise', 'transection'], permission: 'report' },
      { label: 'Item Report', to: '/reports/items', icon: 'package', keywords: ['item'], permission: 'report' },
      { label: 'Store Wise Report', to: '/reports/stores', icon: 'store', keywords: ['store', 'summary'], permission: 'report' },
      { label: 'Expense Report', to: '/reports/expenses', icon: 'trending-down', keywords: ['expense'], permission: 'report' },
      { label: 'Disbursement Report', to: '/reports/disbursements', icon: 'banknote', keywords: ['disbursement'], permission: 'report' },
      { label: 'Order Report', to: '/reports/orders', icon: 'shopping-cart', keywords: ['order'], permission: 'report' },
      { label: 'Admin Tax Report', to: '/reports/tax', icon: 'percent', keywords: ['tax'], permission: 'report' },
      { label: 'Vendor Vat Report', to: '/reports/vendor-tax', icon: 'receipt', keywords: ['vendor', 'vat'], permission: 'report' },
      { label: 'Parcel Tax Report', to: '/reports/parcel-tax', icon: 'percent', keywords: ['parcel', 'tax'], permission: 'report' },
    ],
  },
  {
    id: 'settings',
    title: 'Settings',
    icon: 'settings',
    items: [
      { label: 'Business Setup', to: '/settings/business', icon: 'building-2' },
      { label: 'Orders', to: '/settings/orders', icon: 'settings-2' },
      { label: 'Payments', to: '/settings/payments', icon: 'credit-card' },
      { label: 'Offline Payments', to: '/settings/offline-payments', icon: 'banknote' },
      { label: 'Third Party', to: '/settings/integrations', icon: 'plug', keywords: ['smtp', 'google', 'recaptcha'] },
      { label: 'Analytics', to: '/settings/analytics', icon: 'chart-pie' },
      { label: 'Email Setup', to: '/settings/email', icon: 'mail' },
      { label: 'Notification', to: '/settings/notifications', icon: 'bell' },
      { label: 'FCM', to: '/settings/fcm', icon: 'smartphone' },
      { label: 'Social Media', to: '/settings/social', icon: 'share-2' },
      { label: 'Landing Page', to: '/settings/landing', icon: 'layout-template' },
      { label: 'Languages', to: '/settings/languages', icon: 'languages' },
      { label: 'Modules', to: '/settings/modules', icon: 'boxes' },
      { label: 'System Addons', to: '/settings/addons', icon: 'blocks' },
      { label: 'Zone', to: '/zones', icon: 'map-pin' },
      { label: 'Surge Price', to: '/surge-pricing', icon: 'trending-up' },
      { label: 'File Manager', to: '/settings/files', icon: 'folder-open' },
      { label: 'About Us', to: '/settings/policies/about', icon: 'book-open' },
      { label: 'Privacy Policy', to: '/settings/policies/privacy', icon: 'shield' },
      { label: 'Terms', to: '/settings/policies/terms', icon: 'scale' },
      { label: 'Refund Policy', to: '/settings/policies/refund', icon: 'rotate-ccw' },
      { label: 'Cancellation Policy', to: '/settings/policies/cancelation', icon: 'x-circle' },
      { label: 'Shipping Policy', to: '/settings/policies/shipping', icon: 'truck' },
      { label: 'Subscription Plans', to: '/subscriptions', icon: 'badge-dollar-sign' },
    ],
  },
];

function itemMatchesModule(item: NavItem, moduleType?: string) {
  return !item.module || !moduleType || item.module.includes(moduleType);
}

export function filterNavForModule(
  sections: NavSection[],
  moduleType?: string,
  granted?: Set<string> | null,
): NavSection[] {
  const can = (key?: string) => {
    if (!key) return true;
    if (!granted) return true;
    return granted.has(key) || granted.has('all') || granted.has('*');
  };
  return sections
    .map((s) => ({
      ...s,
      items: s.items
        .filter((i) => itemMatchesModule(i, moduleType) && can(i.permission))
        .map((i) => ({
          ...i,
          children: i.children?.filter((c) => itemMatchesModule(c, moduleType) && can(c.permission)),
        })),
    }))
    .filter((s) => s.items.length > 0);
}

/**
 * Filter sidebar sections by the active global navbar module.
 * Finance (`transactions`) only appears under Transactions & Reports.
 * When that module is active, only the Finance section is returned.
 */
export function filterNavForGlobalModule(
  sections: NavSection[],
  globalModuleId?: string,
): NavSection[] {
  if (!globalModuleId) return sections;
  if (globalModuleId === 'transactions') {
    // Blade _sidebar_transactions: Finance (Business Management) + Report and Analytics
    return sections.filter((s) => s.id === 'transactions' || s.id === 'reports');
  }
  if (globalModuleId === 'dispatch') {
    // Operational control center: only Live Tracking + Orders
    return sections.filter((s) => s.id === 'dispatch');
  }
  return sections.filter((s) => {
    if (!s.module) return true;
    const allowed = Array.isArray(s.module) ? s.module : [s.module];
    return allowed.includes(globalModuleId);
  });
}

function itemHaystack(sectionTitle: string, item: NavItem): string {
  return [item.label, sectionTitle, ...(item.keywords ?? []), ...(item.children ?? []).map((c) => c.label)]
    .join(' ')
    .toLowerCase();
}

/** Case-insensitive search across label + keywords + section title. */
export function searchNav(sections: NavSection[], query: string): NavSection[] {
  const q = query.trim().toLowerCase();
  if (!q) return sections;
  return sections
    .map((s) => {
      const sectionHit = s.title.toLowerCase().includes(q);
      const items = s.items
        .map((item) => {
          if (sectionHit) return item;
          const parentHit = itemHaystack(s.title, item).includes(q);
          const children = item.children?.filter((c) =>
            itemHaystack(s.title, c).includes(q),
          );
          if (parentHit) {
            return { ...item, children: children ?? item.children };
          }
          if (children && children.length) {
            return { ...item, children };
          }
          return null;
        })
        .filter(Boolean) as NavItem[];
      return { ...s, items };
    })
    .filter((s) => s.items.length > 0);
}

export function flattenNavItems(sections: NavSection[]): NavItem[] {
  return sections.flatMap((s) => s.items.flatMap((i) => [i, ...(i.children ?? [])]));
}
